import * as React from "react";
import { ScrollView, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useRouter } from "expo-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, FormProvider, useForm } from "react-hook-form";
import * as z from "zod";

import { displayNameToSlug, FieldSync } from "~/hooks/useFieldSync";
import { isWeb } from "~/lib/constants";
import { generateObjectId } from "~/lib/utils";
import { useAccountStore } from "~/stores/space";
import { CollectionUpdateParams } from "~/stores/space/store";
import { Button } from "~/ui/Button";
import { TextInput } from "~/ui/TextInput";

const collectionFormSchema = z.object({
  title: z.string().min(1, "Title is required."),
  slug: z.string().min(1, "Slug is required."),
  description: z.string().optional().nullable(),
});

type CollectionFormValues = z.infer<typeof collectionFormSchema>;

export function CollectionForm({
  inBottomSheet,
  spaceId,
  collectionId,
  onSuccess,
}: {
  spaceId: string;
  collectionId?: string;
  defaultValues?: CollectionUpdateParams;
  inBottomSheet?: boolean;
  onSuccess?: () => void;
}) {
  const { store: account } = useAccountStore();
  const space = account.spaceById(spaceId);
  const collection = collectionId ? space?.collections.get(collectionId) : null;

  const validator = collectionFormSchema.refine(
    (args) => {
      const collectionWithSameSlug = space?.collectionBySlug(args.slug);

      return (
        !collectionWithSameSlug || collectionWithSameSlug.id === collection?.id
      );
    },
    {
      message: "Slug is already in use.",
      path: ["slug"],
    },
  );

  const form = useForm<CollectionFormValues>({
    resolver: zodResolver(validator),
    mode: "onBlur",
    defaultValues: {
      // TODO: Should this be displayName as well?
      title: "",
      description: "",
      slug: "",
      ...collection,
    },
  });
  const { control, handleSubmit, setFocus } = form;

  const { push } = useRouter();

  function onSubmit(data: CollectionFormValues) {
    if (!data || !space) return;
    if (collectionId) {
      account.updateCollection({ spaceId, collectionId, set: data });
    } else {
      account.createCollection({
        spaceId,
        set: { id: generateObjectId(), ...data },
      });
    }

    if (isWeb) {
      push(`/${account.slug}/${space?.slug}/saves`);
    } else {
      push(`/${account.slug}/${space?.slug}`);
    }
    onSuccess?.();
  }

  const safeInsets = useSafeAreaInsets();

  return (
    <FormProvider {...form}>
      <ScrollView>
        <View
          className="container gap-4 py-2"
          style={{
            paddingBottom: safeInsets.bottom,
          }}
        >
          <Controller
            control={control}
            name="title"
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                inBottomSheet={inBottomSheet}
                label="Title"
                onChangeText={field.onChange}
                value={field.value ?? ""}
                errorMessage={error?.message}
                autoFocus
                enterKeyHint="next"
                onSubmitEditing={() => {
                  setFocus("slug");
                }}
              />
            )}
          />

          <FieldSync
            source="title"
            destination="slug"
            transformFn={displayNameToSlug}
          />

          <Controller
            control={control}
            name="slug"
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                inBottomSheet={inBottomSheet}
                label="Slug"
                onChangeText={field.onChange}
                value={field.value}
                errorMessage={error?.message}
                autoCapitalize="none"
                enterKeyHint="next"
                onSubmitEditing={() => {
                  setFocus("description");
                }}
              />
            )}
          />

          <Controller
            control={control}
            name="description"
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                inBottomSheet={inBottomSheet}
                multiline
                numberOfLines={4}
                label="Description"
                onChangeText={field.onChange}
                value={field.value ?? ""}
                errorMessage={error?.message}
                enterKeyHint="enter"
              />
            )}
          />

          <View />

          <Button onPress={handleSubmit(onSubmit)}>
            {collection ? "Update collection" : "Create collection"}
          </Button>
          <View />
        </View>
      </ScrollView>
    </FormProvider>
  );
}
